import {
    Component,
    OnDestroy,
    Input,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of, Subject } from 'rxjs';
import { concatMap, delay, takeUntil } from 'rxjs/operators';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import {
    FuseNavigationService,
    FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { Navigation } from 'app/core/navigation/navigation.types';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { User } from 'app/core/user/user.types';
import { UserService } from 'app/core/user/user.service';
import { PermissionResponse } from '@penpencil/common';
import {
    CoreService,
    PermissionService,
    BreadscrumbService,
} from 'app/core/services';

@Component({
    selector: 'classy-layout',
    templateUrl: './classy.component.html',
    encapsulation: ViewEncapsulation.None,
})
export class ClassyLayoutComponent implements OnInit, OnDestroy {
    @Input() tooltip: string;
    isScreenSmall: boolean;
    navigation: Navigation;
    user: User;
    user$: Observable<User>;
    title$: Observable<string>;
    breadsCrumb$: Observable<Array<any>>;
    hideHeader: boolean = false;

    permissionsArray: string[];
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _router: Router,
        private _navigationService: NavigationService,
        private _userService: UserService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _coreService: CoreService,
        private _permissionService: PermissionService,
        private _breadscrumbService: BreadscrumbService,
    ) {
        this.title$ = this._breadscrumbService.activeTitle;
        this.breadsCrumb$ = this._breadscrumbService
            .getBreadsCrumb$()
            .pipe(delay(0));
        this.breadsCrumb$.subscribe((response) => {
            if (
                response.length > 0 &&
                response[response.length - 1].hideHeader
            ) {
                this.hideHeader = true;
            } else {
                this.hideHeader = false;
            }
        });

        this.user$ = this._userService.user$.pipe(
            concatMap((user) => {
                this.user = user;
                let query = {};
                if (user?.roles?.find((role) => role?.name === 'Teacher')) {
                    query = {
                        level: 'user',
                    };
                }
                this.permissionsArray = this._permissionService
                    .getPermissions()
                    .getValue();
                if (!this.permissionsArray?.length) {
                    return this._coreService.permissions(query);
                } else {
                    return of(null);
                }
            }),
            concatMap((permissions: PermissionResponse) => {
                const data = permissions?.data?.map((el) => el?.actionName);
                if (data?.length) {
                    this._permissionService.setPermissions(data);
                    this.permissionsArray = data;
                }
                this.ngOnInit();
                return of(this.user);
            }),
        );
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
                this.navigation.default.forEach(
                    (item) => (item['permissions'] = this.permissionsArray),
                );
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }
    logout(): void {
        this._router.navigateByUrl('/sign-out');
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation =
            this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
                name,
            );

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }
}
